import type { ReactElement } from 'react';
import type { GameId } from '@ezishizi/learning-core';

const BRAND = 'var(--color-brand)';
const ACCENT = 'var(--color-accent)';
const SOFT = 'var(--color-brand-soft)';
const INK = 'var(--color-ink)';

const line = {
  fill: 'none',
  stroke: BRAND,
  strokeWidth: 3.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

/**
 * A little picture of each game for the "pick your game" chooser: one flat drawing in the app's
 * two colours, 48 × 48. They stand for the board, not for any word, so nothing here gives an
 * answer away.
 */
const GLYPHS: Partial<Record<GameId, () => ReactElement>> = {
  bubbles: () => (
    <>
      <circle cx={16} cy={30} r={11} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={33} cy={17} r={8} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={34} cy={36} r={5} fill={ACCENT} />
      <path d="M11 26 q2 -4 6 -4" {...line} strokeWidth={2.4} />
    </>
  ),
  forks: () => (
    <>
      <path d="M24 44 V28 M24 28 L10 12 M24 28 L38 12" {...line} />
      <path d="M13 42 q11 6 22 0 l-3 -7 h-16 z" fill={ACCENT} />
    </>
  ),
  trio: () => (
    <>
      <ellipse cx={13} cy={32} rx={9} ry={6} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <ellipse cx={35} cy={32} rx={9} ry={6} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <ellipse cx={24} cy={16} rx={9} ry={6} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={24} cy={14} r={3} fill={ACCENT} />
    </>
  ),
  feed: () => (
    <>
      <path
        d="M6 24 h36 q-2 16 -18 16 q-16 0 -18 -16 z"
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
      />
      <circle cx={18} cy={16} r={4} fill={ACCENT} />
      <circle cx={30} cy={14} r={4} fill={ACCENT} />
    </>
  ),
  find_all: () => (
    <>
      <circle cx={21} cy={21} r={12} fill={SOFT} stroke={BRAND} strokeWidth={3.2} />
      <path d="M30 30 L42 42" {...line} strokeWidth={5} />
      <circle cx={21} cy={21} r={3.5} fill={ACCENT} />
    </>
  ),
  match_pairs: () => (
    <>
      <rect x={4} y={8} width={14} height={14} rx={4} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <rect
        x={30}
        y={26}
        width={14}
        height={14}
        rx={4}
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
      />
      <path d="M18 15 C28 15 20 33 30 33" {...line} stroke={ACCENT} />
    </>
  ),
  sort_meaning: () => (
    <>
      <path
        d="M4 26 h18 l-2 16 h-14 z"
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
        strokeLinejoin="round"
      />
      <path
        d="M26 26 h18 l-2 16 h-14 z"
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
        strokeLinejoin="round"
      />
      <circle cx={13} cy={12} r={5} fill={ACCENT} />
      <rect x={30} y={7} width={10} height={10} rx={3} fill={BRAND} />
    </>
  ),
  count: () => (
    <>
      <circle cx={12} cy={14} r={5} fill={ACCENT} />
      <circle cx={26} cy={14} r={5} fill={ACCENT} />
      <circle cx={40} cy={14} r={5} fill={ACCENT} />
      <rect x={8} y={26} width={32} height={14} rx={7} fill={SOFT} stroke={BRAND} strokeWidth={3} />
    </>
  ),
  tone_paint: () => (
    <>
      <path d="M6 36 C16 36 20 10 42 12" {...line} strokeWidth={6} stroke={ACCENT} />
      <path d="M6 36 C16 36 20 10 42 12" {...line} strokeWidth={2} />
    </>
  ),
  tone_slide: () => (
    <>
      <path d="M6 34 H20 L34 14 H44" {...line} />
      <circle cx={27} cy={24} r={6} fill={ACCENT} />
    </>
  ),
  sort_sound: () => (
    <>
      <path
        d="M24 40 L6 22 Q24 4 42 22 Z"
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
        strokeLinejoin="round"
      />
      <path d="M24 40 V14 M24 40 L13 17 M24 40 L35 17" {...line} strokeWidth={2} />
    </>
  ),
  reveal: () => (
    <>
      <rect x={6} y={10} width={30} height={30} rx={6} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <path d="M14 32 L34 12" {...line} stroke={ACCENT} strokeWidth={5} />
      <path d="M34 12 l4 -4 l3 3 l-4 4 z" fill={INK} />
    </>
  ),
  flash: () => (
    <>
      <path
        d="M27 4 L10 27 h11 l-3 17 l19 -26 h-12 z"
        fill={ACCENT}
        stroke={BRAND}
        strokeWidth={2.4}
        strokeLinejoin="round"
      />
    </>
  ),
  stroke_order: () => (
    <>
      <circle cx={12} cy={14} r={7} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={36} cy={14} r={7} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={24} cy={36} r={7} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <path
        d="M12 14 L36 14 L24 36"
        {...line}
        stroke={ACCENT}
        strokeWidth={2.4}
        strokeDasharray="1 5"
      />
    </>
  ),
  stroke_missing: () => (
    <>
      <path d="M6 14 H42" {...line} />
      <path d="M24 14 V42" {...line} strokeDasharray="2 7" stroke={ACCENT} strokeWidth={4} />
    </>
  ),
  bridge: () => (
    <>
      <path d="M4 34 Q24 6 44 34" {...line} />
      <path d="M12 26 V38 M24 20 V38 M36 26 V38" {...line} stroke={ACCENT} />
    </>
  ),
  scene: () => (
    <>
      <rect x={5} y={8} width={38} height={32} rx={7} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <circle cx={32} cy={19} r={5} fill={ACCENT} />
      <path d="M5 36 L17 24 L27 33 L33 28 L43 38" {...line} strokeWidth={2.6} />
    </>
  ),
  build: () => (
    <>
      <rect x={6} y={26} width={16} height={16} rx={3} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <rect
        x={26}
        y={26}
        width={16}
        height={16}
        rx={3}
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
      />
      <rect x={16} y={6} width={16} height={16} rx={3} fill={ACCENT} />
    </>
  ),
  builder: () => (
    <>
      <rect x={5} y={10} width={18} height={28} rx={4} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      <rect x={27} y={10} width={16} height={12} rx={4} fill={ACCENT} />
      <rect
        x={27}
        y={26}
        width={16}
        height={12}
        rx={4}
        fill={SOFT}
        stroke={BRAND}
        strokeWidth={3}
      />
    </>
  ),
};

export function GameGlyph({ game, className = 'h-16 w-16' }: { game: GameId; className?: string }) {
  const Glyph = GLYPHS[game];
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden>
      {Glyph ? (
        <Glyph />
      ) : (
        <circle cx={24} cy={24} r={16} fill={SOFT} stroke={BRAND} strokeWidth={3} />
      )}
    </svg>
  );
}
